// Copyright (c) Docugami, Inc. All rights reserved.

import { useWorkPathCacheContextProvider, WorkPathCacheContext } from 'components/work_path/WorkPathCache';
import { WorkPathCreationModal } from 'components/work_path/WorkPathCreationModal';
import { useComponentUsageMetrics } from 'hooks/ComponentUsageMetrics';
import { useWorkPathTelemetryContext } from 'hooks/TelemetryContext';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DocSetCollectionType, WorkPath, WorkPathType } from 'networking/WorkPathApi';
import * as queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useGetLabelsQuery } from 'store/frontend/frontendSlice';
import { setCurrentWorkPath, useGetWorkPathsQuery } from 'store/work_path/workPathSlice';
import styled from 'styled-components';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { getAutomationIdForWorkPathType } from 'utils/Automation';
import { DocumentContext, WorkPathCanvas } from 'work_path/WorkPathCanvas';

const WorkPathPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const WorkPathStepContainer = styled.div`
    flex: 1 1 100%;
    height: 100%;
`;

interface NewWorkPathPageProps {
    type: string;
}

/**
 * Landing page when creating a new work path. This component is responsible for creating the transient work path
 * and re-directing to the work path page
 * @param props
 */
export const NewWorkPathPage = (): JSX.Element => {
    const params = useParams<NewWorkPathPageProps>();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const type = WorkPathType[params.type as keyof typeof WorkPathType];
    const docSetCollectionFilter = (queryParams.get('docSetsFilter') as DocSetCollectionType || null) ?? DocSetCollectionType.All;

    return <WorkPathCreationModal collectionFilter={docSetCollectionFilter} type={type} docSetId={queryParams.get('docSetId') ?? undefined} />
}

interface WorkPathPageProps {
    id: string;
}

/**
 * The high level work path page responsible for ensuring the current url param workpath has been fetched
 */
export const WorkPathPage = (): JSX.Element => {
    const paramWorkPathId = useParams<WorkPathPageProps>().id;

    const { isSuccess, isError, currentData: workPaths } = useGetWorkPathsQuery();

    const currentWorkPath = useAppSelector(state => state.workPaths.currentWorkPath);

    const history = useHistory();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isSuccess) {
            const currentWorkPath = workPaths?.entities[paramWorkPathId];
            dispatch(setCurrentWorkPath(currentWorkPath));
        }
        else if (isError) {
            history.replace('/oops');
        }
    }, [isSuccess, isError, workPaths, dispatch, history, paramWorkPathId])

    return !!currentWorkPath ? <WorkPathContainer workPath={currentWorkPath} /> : <></>
}

function getCurrentDocumentContext(location: any): DocumentContext {
    const values = queryString.parse(location.search);
    const documentId = !!values['documentId'] ? (values['documentId'] as string) : '';
    const instanceId = !!values['instanceId'] ? (values['instanceId'] as string) : '';
    return { documentId: documentId, instanceId: instanceId };
}

interface WorkPathContainerProps {
    workPath: WorkPath;
}

/**
 * The core container for interacting with work paths. Contains the menu, step list, canvas, and task pane
 */
const WorkPathContainer = (props: WorkPathContainerProps): JSX.Element => {
    const location = useLocation();

    const [documentContext, setDocumentContext] = useState<DocumentContext>(getCurrentDocumentContext(location));

    useGetLabelsQuery(props.workPath.clusterId);

    const workPathCache = useWorkPathCacheContextProvider();

    useComponentUsageMetrics('WorkPathPage');
    useWorkPathTelemetryContext();

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.WorkPath);
    }, []);

    // Update the document context from our query params
    useEffect(() => {
        setDocumentContext(getCurrentDocumentContext(location))
    }, [location]);

    return (
        <WorkPathCacheContext.Provider value={workPathCache}>
            <WorkPathPageContainer data-automation-id={getAutomationIdForWorkPathType(props.workPath.workPathType)}>
                <WorkPathStepContainer>
                    <WorkPathCanvas
                        workPath={props.workPath}
                        documentContext={documentContext} />
                </WorkPathStepContainer>
            </WorkPathPageContainer>
        </WorkPathCacheContext.Provider>
    )
}
